import React from 'react';
import PropTypes from 'prop-types';
import { useMutation } from '@apollo/client';
import { Modal, Form, Input, Row, Col, message, Button } from 'antd';

import { validateEmail } from '~helpers/validate';
import { URL_REGEXP } from '../constants';
import { MUTATION_CREATE_PMS, MUTATION_UPDATE_PMS } from '../quires';

function PMSModal({ t, onClose, visible, form, type, refetch, pmsAccount }) {
  const { getFieldDecorator, resetFields, validateFields } = form;

  const [createPMSAccount, { loading: createLoading }] = useMutation(MUTATION_CREATE_PMS, {
    onCompleted: result => {
      if (result?.createPMS?.pms?.id) {
        resetFields();
        onClose();
        refetch();
      }
    },
    onError: e => {
      if (e) {
        message.error(e.toString());
      } else {
        message.error(t('Something wrong, please try again.'));
      }
    },
  });

  const [updatePMSAccount, { loading: updateLoading }] = useMutation(MUTATION_UPDATE_PMS, {
    onCompleted: result => {
      if (result?.updatePMS?.pms?.id) {
        resetFields();
        onClose();
        refetch();
      }
    },
    onError: e => {
      if (e) {
        message.error(e.toString());
      } else {
        message.error(t('Something wrong, please try again.'));
      }
    },
  });

  const handleConfirmClick = () => {
    validateFields((err, values) => {
      if (!err) {
        const { emailAddress, name, callbackUrl, note } = values;
        const params = {
          variables: {
            input: {
              id: pmsAccount.id,
              name,
              emailAddress,
              callbackUrl,
              note,
            },
          },
        };

        if (type === 'edit') {
          updatePMSAccount(params);
        } else {
          createPMSAccount(params);
        }
      }
    });
  };

  return (
    <Modal
      visible={visible}
      onCancel={() => {
        resetFields();
        onClose();
      }}
      width={812}
      wrapClassName="pms-modal"
      footer={null}
      confirmLoading={createLoading || updateLoading}
    >
      <div className="pms-modal__content">
        <h3 className="pms-modal__title">
          {type === 'create'
            ? t('cms:cms.pms_integrations.list.pms_modal.create_title')
            : t('cms:cms.pms_integrations.list.pms_modal.edit_title')}
        </h3>
        <Form>
          <Row gutter={24} style={{ paddingLeft: 60, paddingRight: 60 }}>
            <Col span={12}>
              <Form.Item label={t('cms:cms.pms_integrations.list.pms_modal.pms_name.label')}>
                {getFieldDecorator('name', {
                  initialValue: pmsAccount?.name,
                  rules: [
                    {
                      required: true,
                      message: t('cms:cms.pms_integrations.list.pms_modal.pms_name.required'),
                    },
                  ],
                })(
                  <Input
                    placeholder={t('cms:cms.pms_integrations.list.pms_modal.pms_name.placeholder')}
                  />,
                )}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item label={t('cms:cms.pms_integrations.list.pms_modal.channel_manager.label')}>
                {getFieldDecorator('emailAddress', {
                  initialValue: pmsAccount?.emailAddress,
                  rules: [
                    {
                      required: true,
                      message: t(
                        'cms:cms.pms_integrations.list.pms_modal.channel_manager.required',
                      ),
                    },
                    {
                      validator: (_, value, callback) => {
                        const validateResult = validateEmail(value);

                        if (value && !validateResult) {
                          return callback(t('cms:cms.properties.edit.error.email'));
                        }
                        return callback();
                      },
                    },
                  ],
                })(
                  <Input
                    placeholder={t(
                      'cms:cms.pms_integrations.list.pms_modal.channel_manager.placeholder',
                    )}
                  />,
                )}
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            label={t('cms:cms.pms_integrations.list.pms_modal.callback_url.label')}
            style={{ paddingLeft: 60, paddingRight: 60 }}
          >
            {getFieldDecorator('callbackUrl', {
              initialValue: pmsAccount?.webhook?.callbackUrl,
              rules: [
                {
                  required: true,
                  message: t('cms:cms.pms_integrations.list.pms_modal.callback_url.required'),
                },
                {
                  validator: (_, value, callback) => {
                    const valid = URL_REGEXP.test(value);
                    if (value && !valid) {
                      return callback('Please input the right URL.');
                    }
                    return callback();
                  },
                },
              ],
            })(
              <Input
                placeholder={t('cms:cms.pms_integrations.list.pms_modal.callback_url.placeholder')}
              />,
            )}
          </Form.Item>
          <Form.Item
            label={t('cms:cms.pms_integrations.list.pms_modal.notes.label')}
            style={{ paddingLeft: 60, paddingRight: 60, paddingBottom: 8 }}
          >
            {getFieldDecorator('note', {
              initialValue: pmsAccount?.note,
              rules: [
                {
                  max: 250,
                  message: t('cms:cms.reconciliation.lead_generation.update.modal.note.error'),
                },
              ],
            })(
              <Input.TextArea
                placeholder={t('cms:cms.pms_integrations.list.pms_modal.notes.placeholder')}
              />,
            )}
          </Form.Item>
          <Form.Item className="footer-bottom">
            <Button
              onClick={() => {
                resetFields();
                onClose();
              }}
              className="cancel-btn"
            >
              {t('cms:cms.tag_modal.header.btn.cancel')}
            </Button>
            <Button type="primary" onClick={handleConfirmClick} className="confirm-btn">
              {t('cms:cms.tag_modal.header.btn.confirm')}
            </Button>
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
}

PMSModal.propTypes = {
  t: PropTypes.func,
  onClose: PropTypes.func,
  visible: PropTypes.bool,
  form: PropTypes.object,
  type: PropTypes.string,
  refetch: PropTypes.func,
  pmsAccount: PropTypes.object,
};

PMSModal.defaultProps = {
  t: () => {},
  onClose: () => {},
  visible: false,
  form: {},
  type: 'create',
  refetch: () => {},
  pmsAccount: {},
};

export default Form.create({
  name: 'PMSModal',
})(PMSModal);
